import { useBoolean, useToast } from '@chakra-ui/react'
import fileDownload from 'js-file-download'
import { createContext, FC, useCallback, useMemo, useState } from 'react'
import { AiOutlineBarChart } from 'react-icons/ai'
import Swal from 'sweetalert2'
import { useImmer } from 'use-immer'

import { validateIfShowColumnInTable } from '@helper/budget/commercial/validateIfShowColumnInTable'
import { paramsReportToSendMapping } from '@helper/budget/conciliation/paramsReportSendMapping'
import { validateGroupName } from '@helper/budget/conciliation/validateGroupName'
import { parseConciliationBudgetData } from '@helper/grid/budget/conciliation'
import { parseConciliationFooterBudgetData } from '@helper/grid/budget/conciliation-header'
import budget from '@service/budget'

import { ILayoutFilter } from '@base/@types/components/Layout/Filters'
import {
  ITableGridAction,
  ITableHeader
} from '@base/@types/components/Layout/TableGrid'
import { ILayoutToolsAction } from '@base/@types/components/Layout/Tools'
import {
  IBudgetConciliationContext,
  IParamsToSend,
  Pagination,
  IUpdateParams
} from '@base/@types/providers/Budget/conciliation'
import { reportFilter } from '@base/variables/filters/budget/conciliationFilter'
import { reportRequest } from '@base/variables/filters/budget/conciliationRequest'
import budgetCommercialHeader from '@base/variables/headers/budget/conciliation'
const ConciliationBudgetProvider: FC = ({ children }) => {
  const toast = useToast()

  const [loading, setLoading] = useState(false)
  const [filters, setFilters] = useBoolean(true)
  const filtersList: ILayoutFilter[] = reportFilter

  const [dataUpdate, updateDataUpdate] = useImmer<any[]>([])

  const [filename, setFilename] = useState('Orçamento Conciliação - Relatório')

  const [data, updateData] = useImmer<any[]>([])
  const [selecteds, setSelecteds] = useImmer([] as any)
  const [query, setQuery] = useState('')
  const [hiddenColumns, _updateHiddenColumns] = useImmer<string[]>([])
  const [params, updateParams] = useImmer<any>(reportRequest)
  const [pagination, updatePagination] = useImmer<Pagination>({
    actualPage: 0,
    rows: 0,
    totalPages: 0,
    totalRows: 0,
    skip: 0
  })
  const [headers, updateHeaders] = useImmer<ITableHeader[]>(
    budgetCommercialHeader
  )
  const [paramsToSend, setParamsToSend] = useState<IParamsToSend>(() => {
    const paramsFormatted = { ...reportRequest }

    return paramsFormatted
  })

  const clearAllFilters = () => {
    const paramsFormatted = { ...reportRequest }

    updateParams({
      ...params,
      empresaIds: false,
      empresaSelectIds: [],
      centroCdLojaIds: true,
      centroCdLojaSelectIds: [],
      categoriasIds: false,
      categoriasSelectIds: [],
      categoriaIds: false,
      categoriaSelectIds: [],
      gerenteCategoriaIds: false,
      gerenteCategoriaSelectIds: [],
      gerenteProdutoIds: false,
      gerenteProdutoSelectIds: [],
      anosIds: false,
      anosSelectIds: [],
      mesesIds: false,
      mesesSelectIds: [],
      historicoIds: false,
      historicoSelectIds: [],
      operacaoIds: false,
      operacaoSelectIds: []
    })

    setParamsToSend(paramsFormatted)
    if (localStorage.getItem('budgetConciliationParams')) {
      localStorage.setItem(
        'budgetConciliationParams',
        JSON.stringify({
          ...params,
          empresaIds: false,
          empresaSelectIds: [],
          centroCdLojaIds: true,
          centroCdLojaSelectIds: [],
          categoriasIds: false,
          categoriasSelectIds: [],
          categoriaIds: false,
          categoriaSelectIds: [],
          gerenteCategoriaIds: false,
          gerenteCategoriaSelectIds: [],
          gerenteProdutoIds: false,
          gerenteProdutoSelectIds: [],
          anosIds: false,
          anosSelectIds: [],
          mesesIds: false,
          mesesSelectIds: [],
          historicoIds: false,
          historicoSelectIds: [],
          operacaoIds: false,
          operacaoSelectIds: []
        })
      )
    }
    if (localStorage.getItem('budgetConciliationParamsRequest')) {
      localStorage.setItem(
        'budgetConciliationParamsRequest',
        JSON.stringify(paramsFormatted)
      )
    }
  }

  const handleUpdateParams = ({ name, value }: IUpdateParams) => {
    updateParams((draft: { [x: string]: string }) => {
      draft[name] = value
    })

    const paramsToSendFormatted = paramsReportToSendMapping({
      name,
      value,
      compareName: name,
      paramsToSend,
      fieldName: validateGroupName(name)
    })

    setParamsToSend(paramsToSendFormatted)

    if (localStorage.getItem('budgetConciliationParams')) {
      const newParams: IParamsToSend[] = JSON.parse(
        localStorage.getItem('budgetConciliationParams') || ''
      )

      localStorage.setItem(
        'budgetConciliationParams',
        JSON.stringify({ ...newParams, [name]: value })
      )
    }
    if (localStorage.getItem('budgetConciliationParamsRequest')) {
      localStorage.setItem(
        'budgetConciliationParamsRequest',
        JSON.stringify(paramsToSendFormatted)
      )
    }
  }
  const handleLocalParams = useCallback(async () => {
    if (localStorage.getItem('budgetConciliationParams')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetConciliationParams') || ''
      )
      updateParams(newParams)
    } else {
      localStorage.setItem('budgetConciliationParams', JSON.stringify(params))
    }
    if (localStorage.getItem('budgetConciliationParamsRequest')) {
      const newParams = JSON.parse(
        localStorage.getItem('budgetConciliationParamsRequest') || ''
      )
      setParamsToSend(newParams)
    } else {
      localStorage.setItem(
        'budgetConciliationParamsRequest',
        JSON.stringify(paramsToSend)
      )
    }
  }, [updateParams, setParamsToSend, params, paramsToSend])
  const toggleColumn = useCallback(
    (index: number, column: number) => {
      const newHeaders = [...headers]

      newHeaders[index].columns[column] = {
        id: newHeaders[index].columns[column].id,
        name: newHeaders[index].columns[column].name,
        show: !newHeaders[index].columns[column].show
      }

      updateHeaders(newHeaders)
    },
    [headers, updateHeaders]
  )

  const actions = useMemo<ILayoutToolsAction[]>(() => {
    return [
      {
        type: 'export',
        exports: [
          {
            type: 'excel',
            entity: 'budgetConciliationAnalitc',
            data,
            headers
          },
          {
            type: 'excel',
            entity: 'budgetConciliationConsolidated',
            data,
            headers
          }
        ]
      },
      {
        type: 'columns',
        data: headers,
        onToggleColumn: toggleColumn
      }
    ]
  }, [data, headers, toggleColumn])

  const handlePageChange = useCallback(
    async (_page: number) => {
      setLoading(true)

      localStorage.setItem(
        '@AlertPurchaseParamsToSend:',
        JSON.stringify(paramsToSend)
      )

      budget
        .getBudgetReport(paramsToSend)
        .then(({ data: { data, pagination } }) => {
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(data)
          })
          updatePagination(pagination)
          setLoading(false)
        })
        .catch(() => updateData([]))
      setLoading(false)
    },
    [paramsToSend, updateData, updatePagination]
  )
  const handleSelect = useCallback(
    async (index: any) => {
      const supply = index['orcamento-id']
      if (!selecteds.includes(supply)) {
        setSelecteds([...selecteds, supply])
      } else {
        setSelecteds(selecteds.filter((prop: any) => prop !== supply))
      }
    },
    [setSelecteds, selecteds]
  )

  const handleSelectAll = () => {
    if (selecteds.length < data.length) {
      const res = []
      for (const item of data) {
        res.push(item.id)
      }
      setSelecteds(res)
    } else {
      setSelecteds([])
    }
  }
  const updateShowTableInTable = useCallback(
    async (data: any[]) => {
      updateData(draft => {
        return draft.filter(item => item.status === 99).concat(data)
      })

      const headersFormatted = validateIfShowColumnInTable(
        headers,
        paramsToSend
      )

      updateHeaders(headersFormatted)
    },
    [updateData, updateHeaders, headers, paramsToSend]
  )

  const getData = useCallback(async () => {
    setLoading(true)

    localStorage.setItem(
      '@BudgetConciliationParamsToSend:',
      JSON.stringify(paramsToSend)
    )

    let newParams = {}
    if (localStorage.getItem('budgetConciliationParamsRequest')) {
      newParams = JSON.parse(
        localStorage.getItem('budgetConciliationParamsRequest') || ''
      )
    } else {
      newParams = paramsToSend
    }

    budget
      .getBudgetConciliation(newParams)
      .then(({ data: { data, pagination } }) => {
        updateShowTableInTable(data)
        updatePagination(pagination)
        setFilters.toggle()
        setLoading(false)
        setFilters.toggle()
      })

      .catch(() => {
        updateData([])
        setLoading(false)
        setFilters.toggle()
      })
  }, [
    paramsToSend,
    updateShowTableInTable,
    updatePagination,
    setFilters,
    updateData
  ])

  const tableInteraction = useMemo<ITableGridAction[]>(() => {
    return [
      {
        type: 'checkbox',
        icon: <AiOutlineBarChart />,
        onChange: handleSelect,
        selecteds: selecteds,
        isMulti: true
      }
    ]
  }, [selecteds, handleSelect])
  const exportDataConsolidated = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    const obj = {
      orcamentoConciliacao: data,
      groupByFilterBudget: {
        year: true,
        month: true,
        company: true,
        store: true,
        compactCategory: true,
        category: true,
        categoryManager: true,
        productManager: true,
        historic: true,
        operation: true
      }
    }
    try {
      const { data: res } = await budget.postExportConciliationConsolidated(obj)
      fileDownload(res, filename + '-consolidado.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [data, toast, filename])

  const exportDataAnalitc = useCallback(async () => {
    Swal.fire({
      title: 'Exportar dados!',
      html: 'Aguarde um momento<b></b>.',
      timerProgressBar: true,
      timer: 99999,
      didOpen: () => {
        Swal.showLoading()
      }
    })
    try {
      const { data: res } = await budget.postExportConciliationAnalityc(
        paramsToSend
      )
      fileDownload(res, filename + '-analitco.csv')
      Swal.close()
    } catch (error: any) {
      const { data } = error.response
      Swal.close()
      const message =
        data.response?.status === 400
          ? data.response?.data?.data[0].message
          : 'Ocorreu um problema interno. Tente novamente mais tarde'
      toast({
        title: 'Atenção.',
        description: message,
        status: 'warning',
        duration: 2000,
        isClosable: true
      })
    }
  }, [paramsToSend, filename, toast])

  const setTypeSelecteds = useCallback(
    async (type: string) => {
      selecteds.map((index: any) => {
        let conciliations = []

        conciliations = data
        const conciliation = conciliations.filter(
          x => x.id === Number(index)
        )[0]
        const idData = conciliations.findIndex(
          (x: any) => x.id === Number(index)
        )
        try {
          try {
            conciliations = [...conciliations] // important to create a copy, otherwise you'll modify state outside of setState call
            conciliations[idData] = {
              ...conciliation,
              tipoConcilicacao: type === '0' ? 'comercial' : 'operacoes'
            }
            const newData = conciliations
            updateData(draft => {
              return draft.filter(item => item.status === 99).concat(newData)
            })
            updateDataUpdate([...dataUpdate, idData])
          } catch (e) {}
        } catch {}
      })
      setSelecteds([])
    },
    [data, updateData, dataUpdate, updateDataUpdate, selecteds, setSelecteds]
  )

  const handleUpdateGrowthWanted = useCallback(
    async (index: number, wanted: number | string) => {
      let conciliations = []

      conciliations = data
      const conciliation = conciliations.filter(x => x.id === Number(index))[0]
      const idData = conciliations.findIndex((x: any) => x.id === Number(index))
      try {
        try {
          conciliations = [...conciliations] // important to create a copy, otherwise you'll modify state outside of setState call
          conciliations[idData] = {
            ...conciliation,
            previsao: wanted,
            alterado: true
          }
          const newData = conciliations
          updateData(draft => {
            return draft.filter(item => item.status === 99).concat(newData)
          })
          updateDataUpdate([...dataUpdate, idData])
        } catch (e) {}
      } catch {}
    },
    [data, updateData, dataUpdate, updateDataUpdate]
  )
  const saveUpdate = useCallback(async () => {
    setLoading(true)
    const uniqueIndexs = dataUpdate.filter((v, i, a) => a.indexOf(v) === i)
    const datasUpdates: any = []
    let auxCount = 0
    uniqueIndexs.forEach(index => {
      if (auxCount === 0) {
        datasUpdates.push({
          ano: data[index].ano ? data[index].ano : 0,
          mes: data[index].mes ? data[index].mes : 0,
          lojaId: data[index].lojaId ? data[index].lojaId : 0,
          categoriaCompactaId: data[index].categoriaCompactaId
            ? data[index].categoriaCompactaId
            : 0,
          tipoConcilicacao: data[index].tipoConcilicacao
            ? data[index].tipoConcilicacao
            : '',
          categoriaId: data[index].categoriaId ? data[index].categoriaId : 0,
          comercial: true,
          operacoes: false,
          previsaoFinal: data[index].previsao
        })
      } else {
        datasUpdates.push({
          ano: data[index].ano ? data[index].ano : 0,
          mes: data[index].mes ? data[index].mes : 0,
          lojaId: data[index].lojaId ? data[index].lojaId : 0,
          categoriaCompactaId: data[index].categoriaCompactaId
            ? data[index].categoriaCompactaId
            : 0,
          tipoConcilicacao: data[index].tipoConcilicacao
            ? data[index].tipoConcilicacao
            : '',
          categoriaId: data[index].categoriaId ? data[index].categoriaId : 0,
          comercial: true,
          operacoes: false,
          previsaoFinal: data[index].previsao
        })
      }
      auxCount++
    })
    budget
      .putConciliationBudget({ orcamentoConciliacao: [...datasUpdates] })
      .then(({ data: { _data } }) => {
        setLoading(false)
        toast({
          title: 'Sucesso.',
          description: 'Salvo edição em Conciliação',
          status: 'success',
          duration: 2000,
          isClosable: true
        })
      })
      .catch(() => {
        setLoading(false)
      })
  }, [setLoading, dataUpdate, toast, data])

  return (
    <ConciliationBudgetContext.Provider
      value={{
        loading,

        filtersList,
        pagination,
        tableInteraction,
        hiddenColumns,
        data: parseConciliationBudgetData(data),
        actions,
        params,
        handlePageChange,

        dataFooter: parseConciliationFooterBudgetData(data),
        headers,
        filename,
        filters,
        query,
        selecteds,
        handleSelectAll,
        handleSelect,
        setFilename,
        exportDataConsolidated,
        exportDataAnalitc,
        setQuery,
        toggleColumn,
        handleUpdateParams,
        dataUpdate,
        saveUpdate,
        handleUpdateGrowthWanted,
        setTypeSelecteds,
        handleLocalParams,
        getData,
        clearAllFilters,

        setFilters
      }}
    >
      {children}
    </ConciliationBudgetContext.Provider>
  )
}

export default ConciliationBudgetProvider
export const ConciliationBudgetContext =
  createContext<IBudgetConciliationContext>({} as IBudgetConciliationContext)
